import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { useGlobalContext } from "../../context/global";
import {
  CUSTOM_VIEW_TYPE,
  DEFAULT_CUSTOM_VIEW_ID,
  buildCustomCollections,
  buildStandardCollections,
  createNamedCustomView,
  getDefaultCustomSeedKeys,
  getMetricColumns,
  getMetricKeysForView,
  getVisibleColumns,
  resolveInitialColumnState,
} from "./columnCustomizationUtils";
import {
  deleteCustomViewOnPage,
  getPageColumnCustomization,
  renameCustomViewOnPage,
  upsertCustomViewOnPage,
} from "./columnCustomizationApi";

/**
 * Reusable column customization state + persistence for any report page.
 * Fetch is platform-wise (once in global). This hook reads/writes one page slice.
 */
export default function usePageColumnCustomization({
  pageId,
  allColumns = [],
  standardViewOptions = [],
  initialViewType = "PERFORMANCE",
  platform,
  seedWithAllMetrics = false,
}) {
  const {
    clientId,
    columnCustomizationConfig,
    setColumnCustomizationConfig,
  } = useGlobalContext();

  const seedOptions = useMemo(
    () => ({ seedWithAllMetrics }),
    [seedWithAllMetrics]
  );

  const lastUrlViewTypeRef = useRef(initialViewType);
  const hasHydratedColumnConfigRef = useRef(false);

  const [currentViewType, setCurrentViewType] = useState(initialViewType);
  const [selectedMetricKeys, setSelectedMetricKeys] = useState(() => {
    if (standardViewOptions.some((option) => option.value === initialViewType)) {
      return getMetricKeysForView(allColumns, initialViewType);
    }
    return getDefaultCustomSeedKeys(allColumns, null, { seedWithAllMetrics });
  });
  const [columnCollectionId, setColumnCollectionId] = useState(
    standardViewOptions.some((option) => option.value === initialViewType)
      ? initialViewType
      : DEFAULT_CUSTOM_VIEW_ID
  );
  const [isSaving, setIsSaving] = useState(false);

  const pageColumnConfig = useMemo(
    () =>
      getPageColumnCustomization(
        columnCustomizationConfig?.value,
        pageId,
        allColumns,
        seedOptions
      ),
    [columnCustomizationConfig, pageId, allColumns, seedOptions]
  );

  const metricColumns = useMemo(
    () =>
      getMetricColumns(allColumns).map((column) => ({
        ...column,
        label: column.shortTitle || column.title,
      })),
    [allColumns]
  );

  const columnCollections = useMemo(
    () => [
      ...buildStandardCollections(standardViewOptions, allColumns),
      ...buildCustomCollections(pageColumnConfig, allColumns, seedOptions),
    ],
    [standardViewOptions, allColumns, pageColumnConfig, seedOptions]
  );

  const visibleTableColumns = useMemo(
    () => getVisibleColumns(allColumns, selectedMetricKeys),
    [allColumns, selectedMetricKeys]
  );

  const applyColumnSelectionLocally = ({
    collectionId,
    selectedColumnKeys,
  }) => {
    setSelectedMetricKeys(selectedColumnKeys);
    setColumnCollectionId(collectionId);
    const isStandard = standardViewOptions.some(
      (option) => option.value === collectionId
    );
    const nextViewType = isStandard ? collectionId : CUSTOM_VIEW_TYPE;
    lastUrlViewTypeRef.current = nextViewType;
    setCurrentViewType(nextViewType);
  };

  const syncFromViewType = (nextViewType) => {
    if (nextViewType === lastUrlViewTypeRef.current) {
      setCurrentViewType(nextViewType);
      return;
    }
    lastUrlViewTypeRef.current = nextViewType;
    const initial = resolveInitialColumnState({
      columns: allColumns,
      viewType: nextViewType,
      pageConfig: pageColumnConfig,
      standardOptions: standardViewOptions,
      seedOptions,
    });
    setCurrentViewType(initial.viewType);
    setSelectedMetricKeys(initial.selectedColumnKeys);
    setColumnCollectionId(initial.collectionId);
  };

  useEffect(() => {
    if (!columnCustomizationConfig || !allColumns?.length) return;
    if (hasHydratedColumnConfigRef.current) return;

    if (currentViewType !== CUSTOM_VIEW_TYPE) {
      hasHydratedColumnConfigRef.current = true;
      return;
    }

    const initial = resolveInitialColumnState({
      columns: allColumns,
      viewType: CUSTOM_VIEW_TYPE,
      pageConfig: pageColumnConfig,
      standardOptions: standardViewOptions,
      seedOptions,
    });
    setSelectedMetricKeys(initial.selectedColumnKeys);
    setColumnCollectionId(initial.collectionId);
    setCurrentViewType(CUSTOM_VIEW_TYPE);
    lastUrlViewTypeRef.current = CUSTOM_VIEW_TYPE;
    hasHydratedColumnConfigRef.current = true;
  }, [
    columnCustomizationConfig,
    allColumns,
    pageColumnConfig,
    currentViewType,
    standardViewOptions,
  ]);

  useEffect(() => {
    hasHydratedColumnConfigRef.current = false;
  }, [clientId, platform]);

  useEffect(() => {
    const validKeys = new Set(
      getMetricColumns(allColumns).map((column) => column.key)
    );
    setSelectedMetricKeys((prev) => {
      const next = (prev || []).filter((key) => validKeys.has(key));
      if (next.length === prev.length) return prev;
      if (next.length) return next;
      if (currentViewType === CUSTOM_VIEW_TYPE) {
        return getDefaultCustomSeedKeys(allColumns, pageColumnConfig, seedOptions);
      }
      return getMetricKeysForView(allColumns, currentViewType);
    });
  }, [allColumns, currentViewType, pageColumnConfig, seedOptions]);

  const handleApply = (payload) => {
    applyColumnSelectionLocally(payload);
  };

  const handleSave = async ({ collectionId, selectedColumnKeys, collection }) => {
    if (collection?.section !== "custom") return;
    setIsSaving(true);
    try {
      const view =
        pageColumnConfig.views.find((item) => item.id === collectionId) || {
          id: collectionId,
          name: collection?.label || "Custom View",
          isDefault: collectionId === DEFAULT_CUSTOM_VIEW_ID,
        };

      const nextConfig = await upsertCustomViewOnPage({
        clientId,
        platform,
        pageId,
        view: { ...view, selectedColumnKeys },
        activeViewId: collectionId,
        existingConfig: columnCustomizationConfig,
        columns: allColumns,
      });
      setColumnCustomizationConfig(nextConfig);
      applyColumnSelectionLocally({ collectionId, selectedColumnKeys });
      toast.success("Custom view saved");
    } catch (error) {
      toast.error(error?.message ?? "Unable to save custom view");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAs = async ({ name, selectedColumnKeys }) => {
    setIsSaving(true);
    try {
      const newView = createNamedCustomView(name, selectedColumnKeys);
      const nextConfig = await upsertCustomViewOnPage({
        clientId,
        platform,
        pageId,
        view: newView,
        activeViewId: newView.id,
        existingConfig: columnCustomizationConfig,
        columns: allColumns,
      });
      setColumnCustomizationConfig(nextConfig);
      applyColumnSelectionLocally({
        collectionId: newView.id,
        selectedColumnKeys,
      });
      toast.success("Custom view created");
    } catch (error) {
      toast.error(error?.message ?? "Unable to create custom view");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRenameView = async ({ collection, name }) => {
    if (!collection || collection.isDefault) return;
    setIsSaving(true);
    try {
      const nextConfig = await renameCustomViewOnPage({
        clientId,
        platform,
        pageId,
        viewId: collection.id,
        name,
        existingConfig: columnCustomizationConfig,
        columns: allColumns,
      });
      setColumnCustomizationConfig(nextConfig);
      toast.success("View renamed");
    } catch (error) {
      toast.error(error?.message ?? "Unable to rename view");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteView = async ({ collection }) => {
    if (!collection || collection.isDefault) return;
    setIsSaving(true);
    try {
      const wasActive =
        columnCollectionId === collection.id ||
        pageColumnConfig.activeViewId === collection.id;
      const nextConfig = await deleteCustomViewOnPage({
        clientId,
        platform,
        pageId,
        viewId: collection.id,
        existingConfig: columnCustomizationConfig,
        columns: allColumns,
        fallbackActiveViewId: null,
      });
      setColumnCustomizationConfig(nextConfig);
      if (wasActive) {
        applyColumnSelectionLocally({
          collectionId: "PERFORMANCE",
          selectedColumnKeys: getMetricKeysForView(allColumns, "PERFORMANCE"),
        });
      }
      toast.success("View deleted");
    } catch (error) {
      toast.error(error?.message ?? "Unable to delete view");
    } finally {
      setIsSaving(false);
    }
  };

  return {
    currentViewType,
    setCurrentViewType,
    selectedMetricKeys,
    columnCollectionId,
    metricColumns,
    columnCollections,
    visibleTableColumns,
    isSaving,
    syncFromViewType,
    lastUrlViewTypeRef,
    handleApply,
    handleSave,
    handleSaveAs,
    handleRenameView,
    handleDeleteView,
  };
}
